import { EngagementMetrics } from '@/types/chatCompletion';

export class EngagementTracker {
  private startTime: number;
  private messageCount: number;
  private scrollEvents: number;
  private copyEvents: number;
  private inactivityPeriods: number[];
  private lastActivityTime: number;
  private activityCheckInterval: NodeJS.Timeout | null;
  private boundScrollHandler: () => void;
  private boundCopyHandler: () => void;

  constructor() {
    this.startTime = Date.now();
    this.messageCount = 0;
    this.scrollEvents = 0;
    this.copyEvents = 0;
    this.inactivityPeriods = [];
    this.lastActivityTime = Date.now();
    this.activityCheckInterval = null;
    
    // Bind event handlers to maintain context
    this.boundScrollHandler = this.handleScroll.bind(this);
    this.boundCopyHandler = this.handleCopy.bind(this);
    
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Track scrolling with throttling
    let scrollThrottle: NodeJS.Timeout | null = null;
    const throttledScrollHandler = () => {
      if (!scrollThrottle) {
        scrollThrottle = setTimeout(() => {
          this.boundScrollHandler();
          scrollThrottle = null;
        }, 100); // Throttle to max once per 100ms
      }
    };
    
    document.addEventListener('scroll', throttledScrollHandler, { passive: true });
    
    // Track copying
    document.addEventListener('copy', this.boundCopyHandler);
    
    // Track inactivity every 5 seconds
    this.activityCheckInterval = setInterval(() => {
      const inactiveTime = (Date.now() - this.lastActivityTime) / 1000;
      if (inactiveTime > 30) {
        // Record the inactivity period
        this.inactivityPeriods.push(Math.round(inactiveTime));
        // Reset the last activity time to prevent duplicate recordings
        this.lastActivityTime = Date.now();
      }
    }, 5000);
  }

  private handleScroll(): void {
    this.scrollEvents++;
    this.updateActivity();
  }

  private handleCopy(): void {
    this.copyEvents++;
    this.updateActivity();
  }

  private updateActivity(): void {
    this.lastActivityTime = Date.now();
  }

  public incrementMessageCount(): void {
    this.messageCount++;
    this.updateActivity();
  }

  public getMetrics(): EngagementMetrics {
    return {
      timeSpent: Math.round((Date.now() - this.startTime) / 1000),
      messageHistory: this.messageCount,
      scrollEvents: this.scrollEvents,
      copyEvents: this.copyEvents,
      inactivityPeriods: this.inactivityPeriods,
      // Omitting userSentiment as it's optional and not needed
    };
  }

  public cleanup(): void {
    // Remove event listeners
    document.removeEventListener('scroll', this.boundScrollHandler);
    document.removeEventListener('copy', this.boundCopyHandler);
    
    // Clear interval
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = null;
    }
  }
}